import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../../../utils/devLogger';
import { toast } from 'react-toastify';
import CmsPopup from '../../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';
import { getAuctionListForOrder, updateLotSequence } from '../../../../lib/api/admin/adminApi';
import { AuctionWithLotsData, LotOrderData } from '../../../../lib/api/admin/adminApi.types';

interface LotOrderManagementPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 성공 후 호출될 콜백 추가
  auctioneerData?: {
    index: number;
    name: string;
  };
  selectedAuctionData?: {
    index: number;
    name: string;
    preBidStartAt?: string; // 사전입찰 시작 시간 추가
  }; // 기본 선택될 경매 정보
}

const LotOrderManagementPopup: React.FC<LotOrderManagementPopupProps> = ({
  isOpen,
  onClose,
  onSuccess, // 새로운 props 추가
  auctioneerData,
  selectedAuctionData, // 새로운 props 추가
}) => {
  
  // 팝업이 열릴 때 전달받은 props 로깅
  useEffect(() => {
    if (isOpen) {
      devLog('=== LotOrderManagementPopup 열림 ===');
      devLog('auctioneerData:', auctioneerData);
      devLog('selectedAuctionData:', selectedAuctionData);
    }
  }, [isOpen, auctioneerData, selectedAuctionData]);
  // 상태 관리
  const [auctionSearchTerm, setAuctionSearchTerm] = useState('');
  const [lotSearchTerm, setLotSearchTerm] = useState('');
  const [auctionList, setAuctionList] = useState<AuctionWithLotsData[]>([]);
  const [allLots, setAllLots] = useState<LotOrderData[]>([]); // 모든 lot 목록
  const [selectedAuction, setSelectedAuction] = useState<AuctionWithLotsData | null>(null);
  const [selectedLots, setSelectedLots] = useState<LotOrderData[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 각 경매별로 배정된 Lot들을 관리하는 상태 (경매 인덱스 -> Lot[] 매핑)
  const [auctionLotAssignments, setAuctionLotAssignments] = useState<Record<number, LotOrderData[]>>({});
  
  // 경매 리스트 스크롤 ref
  const auctionListRef = React.useRef<HTMLDivElement>(null);

  // API에서 데이터 로드
  useEffect(() => {
    const loadAuctionData = async () => {
      if (!auctioneerData?.index) return;
      
      setLoading(true);
      try {
        // 사전입찰이 실제로 시작된 경우에만 auctionIndex를 전달
        let auctionIndexParam: number | undefined = undefined;
        
        if (selectedAuctionData && selectedAuctionData.index && selectedAuctionData.preBidStartAt) {
          // 사전입찰 시작 시간이 현재 시간보다 과거인지 확인
          const preBidStartDate = new Date(selectedAuctionData.preBidStartAt);
          const currentDate = new Date();
          const isPreBidStarted = preBidStartDate <= currentDate;
          
          if (isPreBidStarted) {
            auctionIndexParam = selectedAuctionData.index;
            devLog('사전입찰이 시작되어 auctionIndex 전달:', auctionIndexParam);
          } else {
            devLog('사전입찰이 아직 시작되지 않아 auctionIndex 전달하지 않음');
          }
        }
        
        devLog('=== API 호출 파라미터 ===');
        devLog('auctioneerIndex:', auctioneerData.index);
        devLog('auctionIndex:', auctionIndexParam);
        devLog('selectedAuctionData:', selectedAuctionData);
        
        const response = await getAuctionListForOrder(auctioneerData.index, auctionIndexParam);
        devLog('API 응답:', response);
        devLog('응답 데이터:', response.data);
        devLog('실제 데이터:', response.data?.data);
        
        if (response.data?.data) {
          const { auctions, lots } = response.data.data;
          devLog('경매 목록:', auctions);
          devLog('Lot 목록:', lots);
          
          setAuctionList(auctions);
          setAllLots(lots);
          
          // 초기 배정 상태 설정 - auctionIndex가 있고 no가 null이 아닌 lot들만
          const initialAssignments: Record<number, LotOrderData[]> = {};
          
          auctions.forEach((auction: AuctionWithLotsData) => {
            // 해당 경매에 배정된 lot들 찾기 (auctionIndex가 매치되고 no가 null이 아닌 것들)
            const assignedLots = lots.filter(
              (lot: LotOrderData) => lot.auctionIndex === auction.index && lot.no !== null
            ).sort((a: LotOrderData, b: LotOrderData) => (a.no || 0) - (b.no || 0));
            
            if (assignedLots.length > 0) {
              initialAssignments[auction.index] = assignedLots;
            }
          });
          
          setAuctionLotAssignments(initialAssignments);
          
          // 기본 경매 선택 로직
          let defaultAuction: AuctionWithLotsData | null = null;
          
          // 1. selectedAuctionData가 있으면 해당 경매를 우선 선택
          if (selectedAuctionData) {
            devLog('=== selectedAuctionData로 경매 찾기 ===');
            devLog('찾을 경매 index:', selectedAuctionData.index);
            devLog('찾을 경매 name:', selectedAuctionData.name);
            devLog('전체 경매 목록:', auctions.map((a: AuctionWithLotsData) => ({ index: a.index, name: a.name })));
            
            defaultAuction = auctions.find(
              (auction: AuctionWithLotsData) => auction.index === selectedAuctionData.index
            ) || null;
            
            devLog('찾은 경매:', defaultAuction ? { index: defaultAuction.index, name: defaultAuction.name } : 'null');
            
            if (!defaultAuction) {
              devWarn('선택된 경매를 목록에서 찾을 수 없습니다.');
              toast.warning('선택된 경매를 찾을 수 없습니다. 경매 목록을 다시 확인해주세요.');
            }
          }
          
          // 2. 해당 경매를 찾지 못했거나 selectedAuctionData가 없으면 첫 번째 경매 선택
          if (!defaultAuction && auctions.length > 0) {
            devLog('=== 첫 번째 경매 선택 (fallback) ===');
            defaultAuction = auctions[0];
          }
          
          // 선택된 경매 설정
          if (defaultAuction) {
            devLog('=== 기본 경매 선택 완료 ===');
            devLog('선택된 경매 index:', defaultAuction.index);
            devLog('선택된 경매 name:', defaultAuction.name);
            
            setSelectedAuction(defaultAuction);
            setSelectedLots(initialAssignments[defaultAuction.index] || []);
            
            // 선택된 경매로 자동 스크롤
            setTimeout(() => {
              devLog('자동 스크롤 실행 - 대상 경매 index:', defaultAuction.index);
              scrollToSelectedAuction(defaultAuction.index);
            }, 100);
          } else {
            devLog('=== 선택할 경매가 없음 ===');
          }
        }
      } catch (error) {
        devError('경매 데이터 로드 실패:', error);
        toast.error('경매 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && auctioneerData) {
      loadAuctionData();
    }
  }, [isOpen, auctioneerData, selectedAuctionData]);

  // 현재 작업 중인 모든 배정 상태를 고려하여 사용 가능한 Lot들 계산
  const getAllAssignedLotIds = () => {
    const assignedIds = new Set<number>();
    
    // 현재 작업 중인 모든 배정들 고려
    Object.entries(auctionLotAssignments).forEach(([auctionIndex, lots]) => {
      // 현재 선택된 경매가 아닌 경우에만 추가 (현재 선택된 경매는 selectedLots로 관리)
      if (!selectedAuction || parseInt(auctionIndex) !== selectedAuction.index) {
        lots.forEach(lot => assignedIds.add(lot.index));
      }
    });
    
    // 현재 선택된 경매의 작업 중인 Lot들 추가
    selectedLots.forEach(lot => assignedIds.add(lot.index));
    
    return assignedIds;
  };

  const assignedLotIds = getAllAssignedLotIds();
  
  // auctionIndex가 null인 Lot들만 사용 가능 (중복 제거)
  const availableLots = (() => {
    const uniqueLotsMap = new Map<number, LotOrderData>();
    
    allLots.forEach(lot => {
      // auctionIndex가 null이고 배정되지 않은 lot이며 disabled가 false인 경우만 사용 가능
      if (lot.auctionIndex === null && !assignedLotIds.has(lot.index) && !lot.disabled) {
        if (!uniqueLotsMap.has(lot.index)) {
          uniqueLotsMap.set(lot.index, lot);
        }
      }
    });
    
    return Array.from(uniqueLotsMap.values());
  })();

  // 필터링된 데이터
  const filteredAuctions = auctionList.filter(auction => {
    return auction.name.toLowerCase().includes(auctionSearchTerm.toLowerCase());
  });

  devLog('현재 상태:', {
    auctionList: auctionList.length,
    auctionSearchTerm,
    filteredAuctions: filteredAuctions.length,
    auctioneerData,
    totalLots: allLots.length,
    availableLotsAfterDedup: availableLots.length,
    assignedLotIds: Array.from(assignedLotIds)
  });

  const filteredLots = availableLots.filter((lot: LotOrderData) => {
    const matchesSearch = lot.name.toLowerCase().includes(lotSearchTerm.toLowerCase());
    const notSelected = !selectedLots.some(selected => selected.index === lot.index);
    return matchesSearch && notSelected;
  });

  const handleSubmit = async () => {
    // 현재 선택된 경매의 작업 내용을 auctionLotAssignments에 저장
    const finalAssignments = { ...auctionLotAssignments };
    if (selectedAuction) {
      finalAssignments[selectedAuction.index] = selectedLots;
    }

    // 모든 경매에 대한 업데이트 데이터 생성
    const updateData: Array<{
      auctionIndex: number;
      lots: Array<{
        lotIndex: number;
        no: number | null;
      }>;
    }> = [];

    // 모든 경매를 순회하며 업데이트 데이터 생성
    auctionList.forEach(auction => {
      // 사전입찰이 시작된 경매는 제외
      if (isPreBidStarted(auction.preBidStartAt)) {
        devLog(`경매 ${auction.name} (${auction.index})는 사전입찰이 시작되어 업데이트에서 제외됩니다.`);
        return;
      }

      const auctionIndex = auction.index;
      const assignedLots = finalAssignments[auctionIndex] || [];
      
      // 해당 경매에 속한 모든 Lot들을 기준으로 업데이트 데이터 생성
      const auctionLots = allLots.filter(lot => lot.auctionIndex === auctionIndex);
      
      const lotsUpdate = auctionLots.map(originalLot => {
        // 현재 배정에서 이 Lot을 찾기
        const assignedLot = assignedLots.find(assigned => assigned.index === originalLot.index);
        
        if (assignedLot) {
          // 배정된 경우: 순서 번호 찾기
          const orderIndex = assignedLots.findIndex(lot => lot.index === originalLot.index);
          return {
            lotIndex: originalLot.index,
            no: orderIndex + 1 // 1부터 시작하는 순서
          };
        } else {
          // 배정되지 않은 경우: null
          return {
            lotIndex: originalLot.index,
            no: null
          };
        }
      });
      
      // 추가로, 새로 배정된 lot들도 처리 (기존에 이 경매에 속하지 않았던 것들)
      assignedLots.forEach(assignedLot => {
        const existsInAuctionLots = auctionLots.some(lot => lot.index === assignedLot.index);
        if (!existsInAuctionLots) {
          const orderIndex = assignedLots.findIndex(lot => lot.index === assignedLot.index);
          lotsUpdate.push({
            lotIndex: assignedLot.index,
            no: orderIndex + 1
          });
        }
      });
      
      // null을 가장 앞으로 정렬 (서버에서 null부터 처리하도록)
      lotsUpdate.sort((a, b) => {
        if (a.no === null && b.no !== null) return -1;
        if (a.no !== null && b.no === null) return 1;
        return 0;
      });
      
      updateData.push({
        auctionIndex,
        lots: lotsUpdate
      });
    });

    devLog('=== Lot 순서 업데이트 데이터 ===');
    devLog('전송할 데이터:', JSON.stringify(updateData, null, 2));
    
    // 요약 정보
    const summary = {
      totalAuctions: updateData.length,
      auctionsWithChanges: updateData.filter(auction => 
        auction.lots.some(lot => lot.no !== null)
      ).length,
      totalLotsWithOrder: updateData.reduce((sum, auction) => 
        sum + auction.lots.filter(lot => lot.no !== null).length, 0
      ),
      totalLotsWithoutOrder: updateData.reduce((sum, auction) => 
        sum + auction.lots.filter(lot => lot.no === null).length, 0
      )
    };
    
    devLog('업데이트 요약:', summary);
    devLog('경매사:', auctioneerData?.name);

    try {
      // API 호출
      devLog('API 요청 데이터:', updateData);
      
      const response = await updateLotSequence(updateData);
      devLog('API 응답:', response);
      
      toast.success(`${auctioneerData?.name}의 Lot 순서가 업데이트되었습니다. (${summary.totalLotsWithOrder}개 배정, ${summary.totalLotsWithoutOrder}개 해제)`);
      
      // 성공 시 onSuccess 콜백 호출
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      devError('Lot 순서 업데이트 실패:', error);
      toast.error('Lot 순서 업데이트에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 사전입찰이 시작되었는지 확인하는 함수
  const isPreBidStarted = (preBidStartAt: string) => {
    const preBidDate = new Date(preBidStartAt);
    const currentDate = new Date();
    return preBidDate <= currentDate;
  };

  const handleAuctionSelect = (auction: AuctionWithLotsData) => {
    devLog('=== 경매 선택 ===');
    devLog('선택한 경매 index:', auction.index);
    devLog('선택한 경매 name:', auction.name);
    devLog('이전 선택된 경매:', selectedAuction?.index);
    devLog('사전입찰 시작 여부:', isPreBidStarted(auction.preBidStartAt));
    
    // 현재 선택된 경매의 작업 내용을 auctionLotAssignments에 저장
    if (selectedAuction) {
      setAuctionLotAssignments(prev => ({
        ...prev,
        [selectedAuction.index]: selectedLots
      }));
    }
    
    // 새로운 경매 선택
    setSelectedAuction(auction);
    
    // 새로운 경매에 대한 기존 배정 내용 로드
    const existingAssignments = auctionLotAssignments[auction.index];
    if (existingAssignments) {
      // 이미 작업 중인 내용이 있으면 그것을 로드
      setSelectedLots(existingAssignments);
      devLog('기존 배정 내용 로드:', existingAssignments.length, '개');
    } else {
      // 작업 중인 내용이 없으면 빈 상태로 시작 (원본 데이터 무시)
      setSelectedLots([]);
      devLog('새로운 경매 - 빈 상태로 시작');
    }
  };

  const handleLotSelect = (lot: LotOrderData) => {
    // 선택된 경매가 없거나 사전입찰이 시작된 경매는 Lot 선택 불가
    if (!selectedAuction || isPreBidStarted(selectedAuction.preBidStartAt)) {
      if (selectedAuction && isPreBidStarted(selectedAuction.preBidStartAt)) {
        toast.warning('사전입찰이 시작된 경매는 Lot을 추가할 수 없습니다.');
      }
      return;
    }
    setSelectedLots(prev => [...prev, lot]);
  };

  const handleLotRemove = (lotIndex: number) => {
    // 사전입찰이 시작된 경매는 Lot 제거 불가
    if (!selectedAuction || isPreBidStarted(selectedAuction.preBidStartAt)) {
      return;
    }
    setSelectedLots(prev => prev.filter(lot => lot.index !== lotIndex));
    devLog(`Lot ${lotIndex} 배정 취소`);
  };

  const handleLotOrderChange = (fromIndex: number, toIndex: number) => {
    // 사전입찰이 시작된 경매는 순서 변경 불가
    if (!selectedAuction || isPreBidStarted(selectedAuction.preBidStartAt)) {
      return;
    }
    setSelectedLots(prev => {
      const newOrder = [...prev];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return newOrder;
    });
  };

  // 선택된 경매로 스크롤하는 함수
  const scrollToSelectedAuction = (auctionIndex: number) => {
    if (!auctionListRef.current) return;
    
    const selectedElement = auctionListRef.current.querySelector(`[data-auction-index="${auctionIndex}"]`);
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  return (
    <CmsPopup
      isOpen={isOpen}
      onClose={onClose}
      title="Lot 배정 및 순서 관리"
      titleComponent={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>Lot 배정 및 순서 관리</span>
          <span style={{ fontSize: '14px', color: '#666' }}>
            선택된 경매사: {auctioneerData?.name || '없음'} (변경불가)
          </span>
        </div>
      }
      isWide={true}
      rightButtons={[
        {
          label: "취소",
          onClick: onClose,
          type: CmsPopupBtnType.SECONDARY
        },
        {
          label: "저장",
          onClick: handleSubmit,
          type: CmsPopupBtnType.PRIMARY
        }
      ]}
    >
      <div style={{ padding: '20px', height: '600px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <div>로딩 중...</div>
          </div>
        ) : (
          /* 3개 섹션을 row 구조로 배치 */
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            height: '100%',
            boxSizing: 'border-box'
          }}>
            
            {/* 첫 번째 섹션: 경매 선택 */}
            <div style={{ 
              flex: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '15px',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minWidth: 0 // flex item의 최소 너비 제한 해제
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: '16px', 
                fontWeight: 'bold',
                borderBottom: '2px solid #f0f0f0',
                paddingBottom: '10px'
              }}>
                경매 선택 
                <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                  ({auctioneerData?.name} 전용)
                </span>
              </h3>
              
              {/* 경매 검색 */}
              <div style={{ 
                marginBottom: '15px',
                position: 'relative'
              }}>
                <input
                  type="text"
                  placeholder={`${auctioneerData?.name} 경매 검색`}
                  value={auctionSearchTerm}
                  onChange={(e) => setAuctionSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>

              {/* 경매 검색 결과 리스트 */}
              <div 
                ref={auctionListRef}
                style={{ 
                  flex: 1,
                  overflow: 'auto',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                {filteredAuctions.length === 0 ? (
                  <div style={{ 
                    padding: '20px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px'
                  }}>
                    {auctioneerData?.name ? (
                      <>
                        {`${auctioneerData.name}의 경매가 없습니다.`}
                        {selectedAuctionData && (
                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#ff6b6b' }}>
                            선택된 경매 (ID: {selectedAuctionData.index})를 찾을 수 없습니다.<br/>
                            경매가 아직 로드되지 않았을 수 있습니다.
                          </div>
                        )}
                      </>
                    ) : 
                      '경매사 정보가 없습니다.'
                    }
                  </div>
                ) : (
                  filteredAuctions.map((auction) => {
                    const isPreBidStartedForAuction = isPreBidStarted(auction.preBidStartAt);
                    return (
                      <div
                        key={auction.index}
                        data-auction-index={auction.index}
                        onClick={() => handleAuctionSelect(auction)}
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          backgroundColor: selectedAuction?.index === auction.index 
                            ? (isPreBidStartedForAuction ? '#f5f5f5' : '#e3f2fd') 
                            : (isPreBidStartedForAuction ? '#f9f9f9' : '#fff'),
                          borderLeft: selectedAuction?.index === auction.index 
                            ? (isPreBidStartedForAuction ? '4px solid #999' : '4px solid #1976d2')
                            : '4px solid transparent',
                          opacity: isPreBidStartedForAuction ? 0.7 : 1
                        }}
                      >
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '14px',
                          marginBottom: '4px',
                          color: isPreBidStartedForAuction ? '#666' : 'inherit'
                        }}>
                          {auction.name}
                          {isPreBidStartedForAuction && (
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '10px', 
                              color: '#999',
                              backgroundColor: '#eee',
                              padding: '2px 6px',
                              borderRadius: '10px'
                            }}>
                              사전입찰 시작
                            </span>
                          )}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: isPreBidStartedForAuction ? '#999' : '#666',
                          marginBottom: '2px'
                        }}>
                          {auction.index}-{new Date(auction.auctionStartAt).toLocaleDateString('sv-SE')}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: isPreBidStartedForAuction ? '#999' : '#999'
                        }}>
                          배정된 Lot: {(() => {
                            // 현재 선택된 경매인 경우 selectedLots 개수, 아니면 auctionLotAssignments에서 가져오기
                            if (selectedAuction?.index === auction.index) {
                              return selectedLots.length;
                            } else if (auctionLotAssignments[auction.index]) {
                              return auctionLotAssignments[auction.index].length;
                            } else {
                              // 기존에 배정된 lot 개수 (auctionIndex가 해당 경매와 매치되고 no가 null이 아닌 것들)
                              return allLots.filter(lot => lot.auctionIndex === auction.index && lot.no !== null).length;
                            }
                          })()}개
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 두 번째 섹션: Lot 선택 */}
            <div style={{ 
              flex: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '15px',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minWidth: 0 // flex item의 최소 너비 제한 해제
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: '16px', 
                fontWeight: 'bold',
                borderBottom: '2px solid #f0f0f0',
                paddingBottom: '10px'
              }}>
                Lot 선택 
                {selectedAuction && isPreBidStarted(selectedAuction.preBidStartAt) ? (
                  <span style={{ fontSize: '14px', color: '#ff6b6b', fontWeight: 'normal' }}>
                    (사전입찰 시작됨)
                  </span>
                ) : (
                  <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                    (사용가능: {availableLots.length}개)
                  </span>
                )}
              </h3>

              {selectedAuction && isPreBidStarted(selectedAuction.preBidStartAt) ? (
                /* 사전입찰이 시작된 경우 안내 문구만 표시 */
                <div style={{ 
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      marginBottom: '8px',
                      color: '#ff6b6b'
                    }}>
                      사전입찰이 시작됨
                    </div>
                    <div>
                      사전입찰이 시작되어<br/>
                      Lot 선택이 불가능합니다.
                    </div>
                    <div style={{ 
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#999'
                    }}>
                      현재 배정된 Lot 순서만 확인 가능합니다.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Lot 검색 */}
                  <div style={{ 
                    marginBottom: '15px',
                    position: 'relative'
                  }}>
                    <input
                      type="text"
                      placeholder="Lot 이름으로 검색"
                      value={lotSearchTerm}
                      onChange={(e) => setLotSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* 사용 가능한 Lot 리스트 */}
                  <div style={{ 
                    flex: 1,
                    overflow: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa'
                  }}>
                    {!selectedAuction ? (
                      <div style={{ 
                        padding: '20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '14px'
                      }}>
                        먼저 경매를 선택해주세요.
                      </div>
                    ) : availableLots.length === 0 && filteredLots.length === 0 ? (
                      <div style={{ 
                        padding: '20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '14px'
                      }}>
                        {auctioneerData?.name}의 사용 가능한 Lot이 없습니다.
                      </div>
                    ) : (
                      filteredLots.map((lot) => (
                        <div
                          key={lot.index}
                          onClick={() => handleLotSelect(lot)}
                          style={{
                            padding: '12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                          }}
                        >
                          <div style={{ 
                            fontWeight: 'bold', 
                            fontSize: '14px',
                            marginBottom: '4px'
                          }}>
                            {lot.name}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999'
                          }}>
                            Lot 인덱스: {lot.index}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {/* 세 번째 섹션: Lot 순서 */}
            <div style={{ 
              flex: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '15px',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minWidth: 0 // flex item의 최소 너비 제한 해제
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: '16px', 
                fontWeight: 'bold',
                borderBottom: '2px solid #f0f0f0',
                paddingBottom: '10px'
              }}>
                Lot 순서 ({selectedLots.length}개)
              </h3>

              {/* 선택된 Lot 순서 리스트 */}
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                border: '1px solid #eee',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                {selectedLots.length === 0 ? (
                  <div style={{ 
                    padding: '20px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px'
                  }}>
                    왼쪽에서 Lot을 선택하여 순서를 지정하세요.
                  </div>
                ) : (
                  selectedLots.map((lot, index) => {
                    const isDisabled = selectedAuction && isPreBidStarted(selectedAuction.preBidStartAt);
                    return (
                      <div
                        key={lot.index}
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #eee',
                          backgroundColor: '#fff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 'bold', 
                            fontSize: '14px',
                            marginBottom: '4px'
                          }}>
                            {index + 1}. {lot.name}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999'
                          }}>
                            Lot 인덱스: {lot.index}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {index > 0 && (
                            <button
                              onClick={() => handleLotOrderChange(index, index - 1)}
                              disabled={!!isDisabled}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
                                color: isDisabled ? '#999' : 'inherit',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.5 : 1
                              }}
                            >
                              ↑
                            </button>
                          )}
                          {index < selectedLots.length - 1 && (
                            <button
                              onClick={() => handleLotOrderChange(index, index + 1)}
                              disabled={!!isDisabled}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
                                color: isDisabled ? '#999' : 'inherit',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.5 : 1
                              }}
                            >
                              ↓
                            </button>
                          )}
                          <button
                            onClick={() => handleLotRemove(lot.index)}
                            disabled={!!isDisabled}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: isDisabled ? '1px solid #ddd' : '1px solid #ff4444',
                              borderRadius: '4px',
                              backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
                              color: isDisabled ? '#999' : '#ff4444',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              opacity: isDisabled ? 0.5 : 1
                            }}
                          >
                            제거
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </CmsPopup>
  );
};

export default LotOrderManagementPopup;
